import { Linkable } from '@/components/blocks/Linkable';
import { Img } from '@/components/blocks/MaterialImage';
import { RenderProductImg } from '@/components/blocks/RenderProductImg';
import { generateBundleDefaultImageUrl } from '@/components/content/Bundle/parts/BundleGallery';
import { TYPES } from '@/data/constants/product';
import { Box, Stack, Typography } from '@mui/material';
import { Price } from '@/data/types/Product';
import { formatPrice } from 'integration/utils/formatPrice';
import { useMemo } from 'react';
import { extractNumberFromString } from 'integration/utils/calculateProductPrices';
import { ProductCardBundlePriceContentRecommendation } from '@/components/content/CatalogEntryList/parts/ProductCardBundlePriceContentRecommendation';
import {
	headerSearchSuggestProductBrandSX,
	headerSearchSuggestProductContainerSX,
	headerSearchSuggestProductContentSX,
	headerSearchSuggestProductDiscountSX,
	headerSearchSuggestProductImgSX,
	headerSearchSuggestProductInfoSX,
	headerSearchSuggestProductNameSX,
	headerSearchSuggestProductPriceSX,
} from '@/components/content/Header/parts/HeaderSearchSuggestProduct/styles/main';

interface InfoElectronico {
	offerDeContado: string | undefined;
	strikedDeContado: string | undefined; // EL QUE SE DEBE DE TACHAR
}

interface Props {
	product: any;
	infoElectronico?: InfoElectronico;
	isInDrawer?: boolean;
	onClick?: () => void;
}

export const HeaderSearchSuggestProduct = ({
	product,
	infoElectronico,
	isInDrawer = false,
	onClick,
}: Props) => {
	const isBundle = product.type === TYPES.bundle;

	const fullImageRaw = isBundle
		? generateBundleDefaultImageUrl(product.partNumber, 1)
		: product.thumbnail;

	const { discount, price } = useMemo(() => {
		if (infoElectronico) {
			return {
				discount: infoElectronico.strikedDeContado
					? formatPrice(infoElectronico.offerDeContado || '', 'MXN')
					: null,
				price: formatPrice(
					infoElectronico.strikedDeContado || infoElectronico.offerDeContado || '',
					'MXN'
				),
			};
		}

		const rawPrice: Price = product.price.filter((item: Price) => item.usage === 'Display')[0];
		const rawDiscount: Price = product.price.filter((item: Price) => item.usage === 'Offer')[0];

		const listPrice = Number(rawPrice.value);
		const offerPrice = Number(rawDiscount.value);

		const withAdditinalOffer = product.ribbons.find(
			(ribbon: any) => ribbon.identifier === 'DPT_Oferta'
		);
		const discoundNumber = extractNumberFromString(withAdditinalOffer?.value ?? '');
		const newOffer = offerPrice - (offerPrice * discoundNumber) / 100;

		// Si el precio de oferta es diferente al de lista, tiene descuento
		const discount = newOffer !== listPrice ? formatPrice(newOffer, rawDiscount.currency) : null;
		const price = formatPrice(rawPrice.value, rawPrice.currency);

		return { discount, price };
	}, [product.price, infoElectronico, product.ribbons]);

	return (
		<Box sx={headerSearchSuggestProductContainerSX(isInDrawer)}>
			<Box sx={headerSearchSuggestProductContentSX(isInDrawer)}>
				<Linkable onClick={onClick} href={product.href}>
					<RenderProductImg
						fullImageRaw={fullImageRaw}
						render={(src) => (
							<Img src={src} sx={headerSearchSuggestProductImgSX(isInDrawer)} alt={product.name} />
						)}
					/>
					<Box sx={headerSearchSuggestProductInfoSX(isInDrawer)}>
						{isBundle ? (
							<ProductCardBundlePriceContentRecommendation
								partNumber={product.partNumber}
								name={product.name}
								manufacturer={product.manufacturer ?? 'Combo'}
								discount={discount}
								price={price}
							/>
						) : (
							<>
								<Box>
									<Typography sx={headerSearchSuggestProductNameSX(isInDrawer)}>
										{product.name}
									</Typography>
									<Typography sx={headerSearchSuggestProductBrandSX(isInDrawer)}>
										{product.manufacturer}
									</Typography>
								</Box>
								<Box display={'flex'} flexDirection={'row'} flexWrap={'wrap'}>
									{discount ? (
										<Typography sx={headerSearchSuggestProductDiscountSX(isInDrawer)}>
											{discount}
										</Typography>
									) : null}
									<Typography sx={headerSearchSuggestProductPriceSX(isInDrawer, discount !== null)}>
										{price}
									</Typography>
								</Box>
							</>
						)}
					</Box>
				</Linkable>
			</Box>
		</Box>
	);
};
